// File: frontend/src/components/form/PhoneInput/PhoneInput.js

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../contexts/LanguageContext';
import FormField from '../FormField/FormField';
import Dropdown from '../Dropdown/Dropdown';
import Modal from '../../Modal/Modal'; // Path to your Modal component
import styles from './PhoneInput.module.css';

const PhoneInput = ({
  id,
  labelKey,
  required,
  value = {},
  onChange,
  hintKey,
  errorKey,
  disabled = false,
  errors,
}) => {
  const { t } = useLanguage();
  const [showTurkeyWarning, setShowTurkeyWarning] = useState(false);

  // Initialize with API-matching structure
  const phoneValue = useMemo(() => ({
    country_phone_code: '+1',
    area_code: '',
    phone_number: '',
    ...value
  }), [value]);

  const countryCodes = [
    { value: '+1', labelKey: 'countryCode.ca', flag: '🇨🇦' },
    { value: '+1 ', labelKey: 'countryCode.us', flag: '🇺🇸' },
    { value: '+31', labelKey: 'countryCode.nl', flag: '🇳🇱' },
    { value: '+33', labelKey: 'countryCode.fr', flag: '🇫🇷' },
    { value: '+34', labelKey: 'countryCode.es', flag: '🇪🇸' },
    { value: '+44', labelKey: 'countryCode.uk', flag: '🇬🇧' },
    { value: '+49', labelKey: 'countryCode.de', flag: '🇩🇪' },
    { value: '+52', labelKey: 'countryCode.mx', flag: '🇲🇽' },
    { value: '+90', labelKey: 'countryCode.tr', flag: '🇹🇷' },
    { value: '+353', labelKey: 'countryCode.ie', flag: '🇮🇪' },
  ];

  // Function to get current pathname from window.location
  const getPathname = () => {
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return '';
  };

  // Determine which warning message to show based on URL path
  const getWarningMessageKey = useCallback(() => {
    const path = getPathname();
    
    if (path.includes('/sign-up')) {
      return 'auth.signUp.turkeyPhoneWarning';
    } else if (path.includes('/sign-in')) {
      return 'auth.signIn.turkeyPhoneWarning';
    } else {
      return 'auth.common.turkeyPhoneWarning';
    }
  }, []);

  // Determine if header should be shown based on URL path
  const shouldShowHeader = useCallback(() => {
    const path = getPathname();
    // Only show header on sign-up and sign-in pages
    return path.includes('/sign-up') || path.includes('/sign-in');
  }, []);

  // Determine modal title based on URL path
  const getWarningTitleKey = useCallback(() => {
    const path = getPathname();
    
    if (path.includes('/sign-up')) {
      return 'auth.signUp.turkeyWarningTitle';
    } else if (path.includes('/sign-in')) {
      return 'auth.signIn.turkeyWarningTitle';
    } else {
      return 'auth.common.turkeyWarningTitle';
    }
  }, []);

  // Show warning modal when Turkey is selected
  useEffect(() => {
    if (phoneValue.country_phone_code === '+90') {
      setShowTurkeyWarning(true);
    }
  }, [phoneValue.country_phone_code]);

  const handleChange = useCallback((field, newValue) => {
    // If switching from Turkey to another country, close the modal
    if (field === 'country_phone_code' && phoneValue.country_phone_code === '+90' && newValue !== '+90') {
      setShowTurkeyWarning(false);
    }
    
    onChange({
      ...phoneValue,
      [field]: newValue,
    });
  }, [onChange, phoneValue]);

  const handleAreaCodeChange = useCallback((e) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 3);
    handleChange('area_code', newValue);
  }, [handleChange]);

  const handlePhoneNumberChange = useCallback((e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 7);
    handleChange('phone_number', digits);
  }, [handleChange]);

  const renderCountryOption = (option) => (
    <>
      <span className={styles.countryFlag}>{option.flag}</span>
      <span className={styles.country_phone_code}>{option.value}</span>
    </>
  );

  const closeModal = () => {
    setShowTurkeyWarning(false);
  };

  // Determine if we should show the header
  const showHeader = shouldShowHeader();

  return (
    <>
      <FormField
        labelKey={labelKey}
        required={required}
        hintKey={hintKey}
        errorKey={errorKey}
        id={id}
        errors={errors}
      >
        <div className={styles.phoneInputContainer}>
          <div className={styles.countryCodeWrapper}>
            <Dropdown
              id={`${id}-country-code`}
              options={countryCodes}
              value={phoneValue.country_phone_code}
              onChange={(newValue) => handleChange('country_phone_code', newValue)}
              disabled={disabled}
              renderOption={renderCountryOption}
              variant="standalone"
            />
          </div>
          <div className={styles.numberWrapper}>
            <input
              id={`${id}-area-code`}
              type="tel"
              placeholder="570"
              maxLength={3}
              value={phoneValue.area_code}
              onChange={handleAreaCodeChange}
              disabled={disabled}
              className={styles.area_code}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              data-form-type="other"
            />
            <input
              id={`${id}-phone-number`}
              type="tel"
              placeholder="2013135"
              maxLength={7}
              value={phoneValue.phone_number}
              onChange={handlePhoneNumberChange}
              disabled={disabled}
              className={styles.phone_number}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              data-form-type="other"
            />
          </div>
        </div>
      </FormField>

      {/* Turkey Warning Modal with dynamic content based on URL */}
      <Modal
        isOpen={showTurkeyWarning}
        onClose={closeModal}
        title={showHeader ? t(getWarningTitleKey()) : null}
        hideHeader={!showHeader}
        variant="warning"
      >
        <div className={styles.modalWarningContent}>
          <p>{t(getWarningMessageKey())}</p>
          <button
            className={styles.primaryButton}
            onClick={closeModal}
            type="button"
          >
            {t('common.understand')}
          </button>
        </div>
      </Modal>
    </>
  );
};

PhoneInput.propTypes = {
  id: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.shape({
    country_phone_code: PropTypes.string,
    area_code: PropTypes.string,
    phone_number: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  hintKey: PropTypes.string,
  errorKey: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default React.memo(PhoneInput);